/* Liquid supplies trusted catalog data; this file owns presentation and cart composition only. */
(() => {
  'use strict';

  const CAMPAIGN_ID = 'build-your-calm-kit-v1';
  const MAX_ITEMS = 4;
  const TIERS = [
    { quantity: 2, percentage: 15 },
    { quantity: 3, percentage: 20 },
    { quantity: 4, percentage: 25 }
  ];
  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => [...root.querySelectorAll(selector)];
  const parseJson = (root, selector, fallback) => {
    try { return JSON.parse($(root, selector)?.textContent || ''); } catch { return fallback; }
  };
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));
  const interpolate = (template, values = {}) => String(template || '').replace(/{{\s*([\w_]+)\s*}}/g, (_, key) => values[key] ?? '');
  const sanitizeGuideHtml = (value) => {
    const allowedTags = new Set(['TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'TH', 'TD', 'CAPTION', 'COLGROUP', 'COL', 'P', 'BR', 'STRONG', 'EM', 'H3', 'UL', 'OL', 'LI']);
    const blockedTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH']);
    const allowedAttributes = new Set(['colspan', 'rowspan', 'scope']);
    const template = document.createElement('template');
    template.innerHTML = String(value || '');
    [...template.content.querySelectorAll('*')].forEach((element) => {
      if (blockedTags.has(element.tagName)) {
        element.remove();
        return;
      }
      if (!allowedTags.has(element.tagName)) {
        element.replaceWith(...element.childNodes);
        return;
      }
      [...element.attributes].forEach((attribute) => {
        if (!allowedAttributes.has(attribute.name.toLowerCase())) element.removeAttribute(attribute.name);
      });
    });
    return template.innerHTML;
  };
  const uuid = () => window.crypto?.randomUUID?.() || `calm-kit-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobile = () => window.matchMedia('(max-width: 749px)').matches;

  const editIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9"></path><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"></path></svg>';
  const removeIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6h18"></path><path d="M8 6V4h8v2"></path><path d="M19 6l-1 14H6L5 6"></path><path d="M10 11v5M14 11v5"></path></svg>';

  const klaviyoFormSelector = 'form[data-testid="klaviyo-form-QU6pVE"]';
  const klaviyoDialogSelector = '[role="dialog"][data-kl-scroll-locking-modal="true"]';
  const klaviyoBodyClass = 'main_page_page-build-your-calm-kit';
  const klaviyoScrollLockClass = 'klaviyo-prevent-body-scrolling';

  /* This suppresses only the known campaign popup on this landing-page template. */
  const klaviyoSuppression = {
    roots: new Set(), observer: null, removedBodyLock: false,
    active() { return this.roots.size > 0 && document.body?.classList.contains(klaviyoBodyClass); },
    dialogs() { return $$(document, klaviyoDialogSelector).filter((dialog) => dialog.querySelector(klaviyoFormSelector)); },
    suppress() {
      if (!this.active()) return;
      const dialogs = this.dialogs();
      if (!dialogs.length) return;
      dialogs.forEach((dialog) => { dialog.dataset.byckKlaviyoSuppressed = 'true'; });
      if (document.body.classList.contains(klaviyoScrollLockClass)) {
        document.body.classList.remove(klaviyoScrollLockClass);
        this.removedBodyLock = true;
      }
    },
    start(root) {
      this.roots.add(root);
      if (this.observer || !this.active()) { this.suppress(); return; }
      this.observer = new MutationObserver(() => this.suppress());
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      this.suppress();
    },
    stop(root) {
      this.roots.delete(root);
      if (this.roots.size) return;
      this.observer?.disconnect();
      this.observer = null;
      const dialogs = this.dialogs().filter((dialog) => dialog.dataset.byckKlaviyoSuppressed === 'true');
      dialogs.forEach((dialog) => { delete dialog.dataset.byckKlaviyoSuppressed; });
      if (this.removedBodyLock && dialogs.some((dialog) => getComputedStyle(dialog).display !== 'none')) {
        document.body.classList.add(klaviyoScrollLockClass);
      }
      this.removedBodyLock = false;
    }
  };

  class CalmKitBuilder {
    constructor(root) {
      this.root = root;
      this.products = parseJson(root, '[data-byck-products]', [])
        .filter((product) => product?.variants?.length)
        .map((product) => window.GroundingPriceTest.mapProduct(product));
      this.gifts = parseJson(root, '[data-byck-gifts]', []).filter((gift) => gift?.variantId);
      this.copy = parseJson(root, '[data-byck-copy]', {});
      this.productByHandle = new Map(this.products.map((product) => [product.handle, product]));
      this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      this.commerceEnabled = root.dataset.commerceEnabled === 'true';
      this.commerceReady = this.commerceEnabled && this.gifts.length === 4 && this.gifts.every((gift) => gift.available);
      this.state = {
        audience: 'adults',
        items: [],
        editingId: null,
        galleryIndex: 0,
        sizeUnit: 'inches',
        kitInstance: uuid(),
        returnFocus: null,
        reopenReview: false,
        reopenOptionsAfterSize: false,
        productMediaIndexes: {}
      };
      this.els = {
        audienceWrap: $(root, '.byck-v2__audience-wrap'),
        selectedTray: $(root, '[data-byck-selected-tray]'),
        selectedItems: $(root, '[data-byck-selected-items]'),
        countdown: $(root, '[data-byck-countdown]'),
        countdownDays: $(root, '[data-byck-countdown-days]'),
        countdownHours: $(root, '[data-byck-countdown-hours]'),
        countdownMinutes: $(root, '[data-byck-countdown-minutes]'),
        countdownSeconds: $(root, '[data-byck-countdown-seconds]'),
        tracker: $(root, '[data-byck-tracker]'),
        miniItems: $(root, '[data-byck-mini-items]'),
        count: $(root, '[data-byck-count]'),
        trackerPrice: $(root, '[data-byck-tracker-price]'),
        reviewButton: $(root, '[data-byck-open-review]'),
        progress: $(root, '[data-byck-progress]'),
        next: $(root, '[data-byck-next]'),
        products: $(root, '[data-byck-products-grid]'),
        summary: $(root, '[data-byck-summary]'),
        summaryItems: $(root, '[data-byck-summary-items]'),
        reward: $(root, '[data-byck-reward]'),
        pricing: $(root, '[data-byck-pricing]'),
        checkout: $(root, '[data-byck-add-to-cart]'),
        cartError: $(root, '[data-byck-cart-error]'),
        optionDialog: $(root, '[data-byck-option-dialog]'),
        optionForm: $(root, '[data-byck-option-form]'),
        optionTitle: $(root, '[data-byck-option-title]'),
        optionPosition: $(root, '[data-byck-option-position]'),
        optionFields: $(root, '[data-byck-option-fields]'),
        optionError: $(root, '[data-byck-option-error]'),
        gallery: $(root, '[data-byck-gallery]'),
        sizeDialog: $(root, '[data-byck-size-dialog]'),
        sizeTitle: $(root, '[data-byck-size-title]'),
        sizeContent: $(root, '[data-byck-size-content]'),
        toast: $(root, '[data-byck-toast]'),
        status: $(root, '[data-byck-status]')
      };
      if (!this.products.length) return;
      klaviyoSuppression.start(root);
      this.bind();
      this.startCountdown();
      this.renderAll();
    }

    /* Shopify escapes ampersands in translated JSON; decode that entity before rendering copy as text. */
    c(key, values) { return interpolate(this.copy[key], values).replaceAll('&amp;', '&'); }

    money(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
      }).format(Number(cents || 0) / 100);
    }

    announce(message) {
      this.els.status.textContent = '';
      window.setTimeout(() => { this.els.status.textContent = message; }, 20);
    }

    toast(message) {
      this.els.toast.textContent = message;
      this.els.toast.hidden = false;
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => { this.els.toast.hidden = true; }, 1400);
    }

    product(handle) { return this.productByHandle.get(handle); }
    itemProduct(item) { return this.product(item.productHandle); }
    itemVariant(item) { return this.itemProduct(item)?.variants.find((variant) => String(variant.id) === String(item.variantId)); }

    imageUrl(src, width) {
      if (!src) return '';
      if (/([?&]width=)\d+/.test(src)) return src.replace(/([?&]width=)\d+/, `$1${width}`);
      return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
    }

    productImage(product, className = '', index = 0, alt = product?.title || '') {
      const media = product?.media?.[index] || product?.media?.[0];
      if (!media?.src) return '<span class="byck-v2__placeholder" aria-hidden="true"></span>';
      const src = this.imageUrl(media.src, 600);
      const srcset = [240, 480, 720].map((width) => `${this.imageUrl(media.src, width)} ${width}w`).join(', ');
      return `<img class="${escapeHtml(className)}" src="${escapeHtml(src)}" srcset="${escapeHtml(srcset)}" sizes="(min-width: 750px) 24vw, 46vw" width="600" height="720" loading="lazy" decoding="async" draggable="false" alt="${escapeHtml(media.alt || alt)}">`;
    }

    startCountdown() {
      if (!this.els.countdown) return;
      const deadline = Date.parse($(this.root, '.byck-v2__hero')?.dataset.byckDeadline || '');
      if (!Number.isFinite(deadline) || deadline <= Date.now()) {
        // An expired campaign clock must disappear rather than advertising 00:00:00:00.
        this.els.countdown.hidden = true;
        return;
      }

      const update = () => {
        const remaining = Math.max(0, deadline - Date.now());
        const values = {
          countdownDays: Math.floor(remaining / 86400000),
          countdownHours: Math.floor(remaining / 3600000) % 24,
          countdownMinutes: Math.floor(remaining / 60000) % 60,
          countdownSeconds: Math.floor(remaining / 1000) % 60
        };
        Object.entries(values).forEach(([key, value]) => {
          if (this.els[key]) this.els[key].textContent = String(value).padStart(2, '0');
        });
        if (remaining === 0) window.clearInterval(this.countdownTimer);
      };

      this.els.countdown.hidden = false;
      update();
      this.countdownTimer = window.setInterval(update, 1000);
    }

    makeItem(productHandle) {
      const assignedProduct = this.product(productHandle);
      return { id: uuid(), productHandle: assignedProduct?.handle || productHandle, selections: [], variantId: null, complete: false };
    }

    tier(count = this.state.items.length) {
      return [...TIERS].reverse().find((tier) => count >= tier.quantity) || { quantity: 0, percentage: 0 };
    }

    totals() {
      const count = this.state.items.length;
      const completeCount = this.state.items.filter((item) => item.complete).length;
      const pending = count - completeCount;
      const prices = this.state.items.reduce((totals, item) => {
        const product = this.itemProduct(item);
        const variant = this.itemVariant(item);
        const price = Number(variant?.price ?? product?.priceMin ?? 0);
        const compareAt = Number(variant?.compareAtPrice ?? product?.compareAtMin ?? 0);
        totals.subtotal += price;
        // A missing, zero, or stale compare-at price must never inflate savings.
        totals.originalSubtotal += compareAt > price ? compareAt : price;
        return totals;
      }, { subtotal: 0, originalSubtotal: 0 });
      const percentage = this.tier(count).percentage;
      const savings = Math.round(prices.subtotal * percentage / 100);
      const total = prices.subtotal - savings;
      const saleSavings = prices.originalSubtotal - prices.subtotal;
      const totalSavings = prices.originalSubtotal - total;
      const totalSavingsPercentage = prices.originalSubtotal > 0
        ? Math.round(totalSavings * 100 / prices.originalSubtotal)
        : 0;
      return {
        count,
        completeCount,
        pending,
        subtotal: prices.subtotal,
        originalSubtotal: prices.originalSubtotal,
        percentage,
        savings,
        saleSavings,
        totalSavings,
        totalSavingsPercentage,
        total
      };
    }

    nextMessage(count) {
      if (count === 0) return this.c('addTwoMoreFifteen');
      if (count === 1) return this.c('addOneMoreFifteen');
      if (count === 2) return this.c('addOneMoreTwenty');
      if (count === 3) return this.c('addOneMoreTwentyFive');
      return this.c('maximumReward');
    }

    selectedOptions(item) {
      const variant = this.itemVariant(item);
      return variant ? variant.options.join(' · ') : this.c('optionsNeeded');
    }

    renderTracker() {
      const totals = this.totals();
      this.els.count.textContent = totals.count === 0
        ? this.c('selectedNone')
        : totals.count === 1
          ? this.c('selectedOne')
          : totals.count >= MAX_ITEMS
            ? this.c('selectedFull')
            : this.c('selectedMany', { count: totals.count });
      // The bar always represents the complete four-hoodie reward ladder.
      this.els.progress.style.width = `${Math.min(100, totals.completeCount * 25)}%`;
      this.els.progress.parentElement.setAttribute('aria-valuemax', '4');
      this.els.progress.parentElement.setAttribute('aria-valuenow', String(totals.completeCount));
      this.els.next.textContent = totals.pending
        ? this.c('confirmRewards', { count: totals.pending })
        : this.nextMessage(totals.completeCount);
      // On mobile the sticky action must expose unfinished configuration before offering review.
      this.els.reviewButton.textContent = totals.pending
        ? this.c('chooseOptionsCount', { count: totals.pending })
        : this.c('reviewBundle');
      this.els.reviewButton.disabled = totals.count === 0;
      this.els.miniItems.innerHTML = this.state.items.map((item, index) => {
        const product = this.itemProduct(item);
        return `<span class="byck-v2__mini${item.complete ? '' : ' is-pending'}" title="${escapeHtml(product.title)}">${this.productImage(product, '', 0, '')}<small>${index + 1}</small></span>`;
      }).join('');
      // Tiny merchant-owned gift images make each mobile milestone scannable without adding another text row.
      $$(this.root, '[data-byck-mark-gifts]').forEach((marker) => {
        const unlockQuantity = Number(marker.dataset.byckMarkGifts);
        marker.innerHTML = this.gifts
          .filter((gift) => gift.unlockQuantity === unlockQuantity && gift.image)
          .map((gift) => `<img src="${escapeHtml(this.imageUrl(gift.image, 64))}" width="32" height="32" loading="lazy" decoding="async" alt="">`)
          .join('');
      });
      const hasSavings = totals.totalSavings > 0;
      const retail = hasSavings ? `<s aria-label="${escapeHtml(this.c('retailTotal'))} ${escapeHtml(this.money(totals.originalSubtotal))}">${escapeHtml(this.money(totals.originalSubtotal))}</s>` : '';
      const savings = hasSavings ? `<span>${escapeHtml(this.c('totalSavings', { amount: this.money(totals.totalSavings), percentage: totals.totalSavingsPercentage }))}</span>` : '';
      this.els.trackerPrice.innerHTML = `${retail}<b>${escapeHtml(this.money(totals.total))}</b>${savings}`;
    }

    renderSelectionTray() {
      const items = this.state.items;
      this.els.selectedTray.hidden = items.length === 0;
      this.els.selectedItems.innerHTML = items.map((item) => {
        const product = this.itemProduct(item);
        const media = product?.media?.[0];
        const image = media?.src
          ? `<img src="${escapeHtml(this.imageUrl(media.src, 120))}" width="60" height="60" loading="lazy" decoding="async" alt="">`
          : '<span class="byck-v2__placeholder" aria-hidden="true"></span>';
        return `<span class="byck-v2__selected-item${item.complete ? '' : ' is-pending'}">
          <button type="button" class="byck-v2__selected-item-edit" data-byck-edit-item="${item.id}" aria-label="${escapeHtml(this.c('edit'))}: ${escapeHtml(product.title)}">
            ${image}<span><b>${escapeHtml(product.title)}</b><small>${escapeHtml(this.selectedOptions(item))}</small></span>
          </button>
          <button type="button" class="byck-v2__selected-item-remove" data-byck-remove-item="${item.id}" aria-label="${escapeHtml(this.c('remove'))}: ${escapeHtml(product.title)}">×</button>
        </span>`;
      }).join('');
    }

    showProductMedia(productHandle, requestedIndex) {
      const product = this.product(productHandle);
      const mediaCount = Math.min(product?.media?.length || 0, 3);
      if (mediaCount < 2) return;
      this.state.productMediaIndexes[productHandle] = (requestedIndex + mediaCount) % mediaCount;
      this.renderProducts();
    }

    moveProductMedia(productHandle, direction) {
      const currentIndex = Number(this.state.productMediaIndexes[productHandle]) || 0;
      this.showProductMedia(productHandle, currentIndex + direction);
    }

    renderProducts() {
      const atMax = this.state.items.length >= MAX_ITEMS;
      const products = this.products.filter((product) => product.audience === this.state.audience);
      this.els.products.innerHTML = products.map((product) => {
        const selectedItems = this.state.items.filter((item) => item.productHandle === product.handle);
        const pendingItems = selectedItems.filter((item) => !item.complete);
        const completeItems = selectedItems.filter((item) => item.complete);
        const quantity = selectedItems.length;
        const badge = product.badge === 'bestseller'
          ? this.c('bestseller')
          : product.badge === 'new-release'
            ? this.c('newRelease')
            : product.badge === 'new' ? this.c('newProduct') : '';
        const cardMedia = (product.media || []).slice(0, 3);
        const mediaIndex = Math.min(Number(this.state.productMediaIndexes[product.handle]) || 0, Math.max(cardMedia.length - 1, 0));
        const dots = cardMedia.map((_, index) => `<button type="button" class="${index === mediaIndex ? 'is-active' : ''}" data-byck-product-media-index="${index}" data-byck-product-handle="${escapeHtml(product.handle)}" aria-label="${escapeHtml(`${product.title} ${index + 1}`)}" aria-current="${index === mediaIndex ? 'true' : 'false'}"></button>`).join('');
        const colorIndex = product.options.findIndex((option) => option.kind === 'color' || /colou?r/i.test(option.name));
        const configuredRows = completeItems.map((item) => {
          const color = colorIndex >= 0 ? item.selections[colorIndex] : '';
          const swatchStyle = color ? product.swatches?.[color] || '' : '';
          const swatch = color ? `<i class="byck-v2__selected-swatch" style="${escapeHtml(swatchStyle)}" aria-hidden="true"></i>` : '';
          return `<button class="byck-v2__configured-option" type="button" data-byck-configure-item="${item.id}" aria-label="${escapeHtml(this.c('edit'))}: ${escapeHtml(this.selectedOptions(item))}"><span>${swatch}<b>${escapeHtml(this.selectedOptions(item))}</b></span><small>${escapeHtml(this.c('editShort'))}</small></button>`;
        }).join('');
        let control = `<button class="byck-v2__plus" type="button" data-byck-add-product="${escapeHtml(product.handle)}" aria-label="${escapeHtml(this.c('addProduct', { product: product.title }))}" ${atMax || !product.available ? 'disabled' : ''}>+</button>`;
        if (quantity > 0) {
          // Selected products still need real options before they can qualify for checkout.
          const configurePending = pendingItems.length
            ? `<button class="byck-v2__configure-product" type="button" data-byck-configure-item="${pendingItems[0].id}">${escapeHtml(product.options.length > 2 ? this.c('chooseAllProductOptions') : this.c('chooseProductOptions'))}</button>`
            : '';
          const quantityControl = pendingItems.length ? '' : `<div class="byck-v2__product-actions"><div class="byck-v2__quantity"><button type="button" data-byck-decrement="${escapeHtml(product.handle)}" aria-label="${escapeHtml(this.c('removeOne'))}">−</button><span>${quantity}</span></div><button class="byck-v2__add-another" type="button" data-byck-add-product="${escapeHtml(product.handle)}" ${atMax ? 'disabled' : ''}>+ ${escapeHtml(this.c('addAnother'))}</button></div>`;
          control = `<div class="byck-v2__configuration">${configuredRows}${configurePending}${quantityControl}</div>`;
        }
        const price = Number(product.priceMin) || 0;
        const compareAt = Number(product.compareAtMin) || 0;
        const originalPrice = compareAt > price
          ? `<s aria-label="${escapeHtml(this.c('originalPrice'))} ${escapeHtml(this.money(compareAt))}">${escapeHtml(this.money(compareAt))}</s>`
          : '';
        const priceLabel = originalPrice ? this.c('salePriceBeforeSavings') : this.c('priceBeforeSavings');
        const priceMarkup = `<p class="byck-v2__product-price">${originalPrice}<strong>${escapeHtml(this.money(price))}</strong><small>${escapeHtml(priceLabel)}</small></p>`;
        return `<article class="byck-v2__product${quantity ? ' is-selected' : ''}${pendingItems.length ? ' is-pending' : ''}">
          <div class="byck-v2__product-media" data-byck-product-media="${escapeHtml(product.handle)}">${this.productImage(product, 'byck-v2__product-image', mediaIndex)}${badge ? `<span class="byck-v2__badge">${escapeHtml(badge)}</span>` : ''}${dots ? `<span class="byck-v2__dots">${dots}</span>` : ''}</div>
          <div class="byck-v2__product-info"><h3>${escapeHtml(product.title)}</h3>${priceMarkup}${product.available ? control : `<strong>${escapeHtml(this.c('soldOut'))}</strong>`}</div>
        </article>`;
      }).join('');
    }

    giftCards(totals) {
      // Show the complete ladder so locked gifts motivate the next hoodie instead of remaining undiscoverable.
      return this.gifts.map((gift) => {
        const unlocked = totals.pending === 0 && totals.count >= gift.unlockQuantity;
        const remaining = Math.max(0, gift.unlockQuantity - totals.count);
        let stateLabel = this.c('confirmOptions');
        if (unlocked) stateLabel = this.c('free');
        else if (remaining === 1) stateLabel = this.c('addOneToUnlock');
        else if (remaining > 1) stateLabel = this.c('addManyToUnlock', { count: remaining });
        const image = gift.image ? `<img src="${escapeHtml(this.imageUrl(gift.image, 240))}" width="240" height="240" loading="lazy" decoding="async" alt="">` : '';
        return `<span class="byck-v2__gift${unlocked ? ' is-unlocked' : ' is-locked'}">${image}<span><b>${escapeHtml(gift.title)}</b><small>${gift.price ? `<s>${this.money(gift.price)}</s> ` : ''}<em>${escapeHtml(stateLabel)}</em></small></span></span>`;
      }).join('');
    }

    rewardHeading(totals) {
      if (totals.pending > 0) return this.c('confirmOptions');
      if (totals.count < 2) return this.nextMessage(totals.count);
      if (totals.count === 2) return this.c('rewardTwo');
      if (totals.count === 3) return this.c('rewardThree');
      return this.c('rewardFour');
    }

    renderSummary() {
      const totals = this.totals();
      this.els.summaryItems.innerHTML = this.state.items.map((item, index) => {
        const product = this.itemProduct(item);
        const variant = this.itemVariant(item);
        const price = Number(variant?.price ?? product?.priceMin ?? 0);
        const compareAt = Number(variant?.compareAtPrice ?? product?.compareAtMin ?? 0);
        const originalPrice = compareAt > price ? `<s>${escapeHtml(this.money(compareAt))}</s>` : '';
        return `<article class="byck-v2__summary-item${item.complete ? '' : ' is-pending'}">
          <span class="byck-v2__summary-thumb">${this.productImage(product, '', 0, '')}</span>
          <span><b>${escapeHtml(product.title)}</b><small>${escapeHtml(this.selectedOptions(item))}</small><em class="byck-v2__summary-price">${originalPrice}<strong>${escapeHtml(this.money(price))}</strong></em></span>
          <span class="byck-v2__item-actions"><button type="button" data-byck-edit-item="${item.id}" aria-label="${escapeHtml(this.c('edit'))}">${editIcon}</button><button type="button" data-byck-remove-item="${item.id}" aria-label="${escapeHtml(this.c('remove'))}">${removeIcon}</button></span>
        </article>`;
      }).join('');
      // Keep one complete reward headline; repeating the same threshold beneath it adds noise.
      this.els.reward.innerHTML = `<strong>${escapeHtml(this.rewardHeading(totals))}</strong><div class="byck-v2__gift-grid">${this.giftCards(totals)}</div>`;
      const originalRow = totals.saleSavings > 0
        ? `<p><span>${escapeHtml(this.c('retailTotal'))}</span><s>${escapeHtml(this.money(totals.originalSubtotal))}</s></p><p><span>${escapeHtml(this.c('saleSubtotal'))}</span><span>${escapeHtml(this.money(totals.subtotal))}</span></p>`
        : `<p><span>${escapeHtml(this.c('saleSubtotal'))}</span><span>${escapeHtml(this.money(totals.subtotal))}</span></p>`;
      const bundleSavingsLabel = totals.percentage
        ? `${this.c('extraBundleSavings')} (${totals.percentage}%)`
        : this.c('extraBundleSavings');
      const totalSavingsRow = totals.totalSavings > 0
        ? `<p class="byck-v2__pricing-savings"><span>${escapeHtml(this.c('totalSavingsLabel', { percentage: totals.totalSavingsPercentage }))}</span><strong>${escapeHtml(this.money(totals.totalSavings))}</strong></p>`
        : '';
      this.els.pricing.innerHTML = `${originalRow}<p><span>${escapeHtml(bundleSavingsLabel)}</span><strong>−${escapeHtml(this.money(totals.savings))}</strong></p>${totalSavingsRow}<p class="byck-v2__pricing-total"><span>${escapeHtml(this.c('bundleTotal'))}</span><strong>${escapeHtml(this.money(totals.total))}</strong></p>`;

      const canCheckout = this.commerceReady && totals.count >= 2 && totals.pending === 0;
      this.els.checkout.disabled = !canCheckout;
      if (!this.commerceEnabled) this.els.checkout.textContent = this.c('previewButton');
      else if (totals.count < 2) this.els.checkout.textContent = this.c('confirmMany', { count: 2 - totals.count });
      else if (totals.pending === 1) this.els.checkout.textContent = this.c('confirmOne');
      else if (totals.pending > 1) this.els.checkout.textContent = this.c('confirmMany', { count: totals.pending });
      else this.els.checkout.textContent = this.c('addBundle', { total: this.money(totals.total) });
    }

    renderAll() {
      this.renderSelectionTray();
      this.renderTracker();
      this.renderProducts();
      this.renderSummary();
    }

    addProduct(handle, trigger) {
      const product = this.product(handle);
      if (!product?.available || this.state.items.length >= MAX_ITEMS) return;
      const item = this.makeItem(handle);
      this.state.items.push(item);
      this.renderAll();
      this.openOptions(item.id, trigger, false);
    }

    decrementProduct(handle) {
      const index = this.state.items.map((item) => item.productHandle).lastIndexOf(handle);
      if (index < 0) return;
      const [removed] = this.state.items.splice(index, 1);
      this.renderAll();
      this.announce(this.c('productRemoved', { product: this.itemProduct(removed)?.title || '' }));
    }

    removeItem(id) {
      const index = this.state.items.findIndex((item) => item.id === id);
      if (index < 0) return;
      const [removed] = this.state.items.splice(index, 1);
      this.renderAll();
      this.announce(this.c('productRemoved', { product: this.itemProduct(removed)?.title || '' }));
    }

    optionCandidates(product, selections, optionIndex) {
      return product.variants.filter((variant) => variant.available && variant.options.every((value, index) => index === optionIndex || !selections[index] || selections[index] === value));
    }

    editingItem() { return this.state.items.find((item) => item.id === this.state.editingId); }

    openOptions(id, trigger, fromReview) {
      const item = this.state.items.find((entry) => entry.id === id);
      const product = this.itemProduct(item);
      if (!item || !product?.available) return;
      this.state.editingId = id;
      this.state.galleryIndex = 0;
      this.state.returnFocus = trigger || document.activeElement;
      this.state.reopenReview = Boolean(fromReview);
      const variant = this.itemVariant(item);
      item.selections = [...(variant?.options || item.selections || [])];
      this.state.galleryIndex = this.galleryStartingIndex(this.galleryImages());
      this.closeReview(false);
      this.els.optionTitle.textContent = product.title;
      this.els.optionPosition.textContent = this.c('hoodiePosition', { current: this.state.items.indexOf(item) + 1, total: this.state.items.length });
      this.els.optionError.hidden = true;
      this.renderOptionFields();
      this.renderGallery();
      if (!this.els.optionDialog.open) this.els.optionDialog.showModal();
      this.els.optionDialog.querySelector('select, button')?.focus();
    }

    renderOptionFields() {
      const item = this.editingItem();
      const product = this.itemProduct(item);
      if (!item || !product) return;

      this.els.optionFields.innerHTML = product.options.map((option, index) => {
        const availableValues = new Set(this.optionCandidates(product, item.selections, index).map((variant) => variant.options[index]));
        const isColor = option.kind === 'color' || /colou?r/i.test(option.name);
        const isSize = option.kind === 'size' || /size/i.test(option.name);
        const selectedValue = item.selections[index] || '';
        const controls = option.values.map((value) => {
          const selected = selectedValue === value;
          const available = availableValues.has(value);
          const common = `type="button" data-byck-option-index="${index}" data-byck-option-value="${escapeHtml(value)}" aria-pressed="${selected}" ${available ? '' : 'disabled'}`;
          if (isColor) {
            const swatchStyle = product.swatches?.[value] || '';
            return `<button class="byck-v2__swatch${selected ? ' is-active' : ''}" ${common} style="${escapeHtml(swatchStyle)}" aria-label="${escapeHtml(value)}" title="${escapeHtml(value)}"><span class="visually-hidden">${escapeHtml(value)}</span></button>`;
          }
          return `<button class="byck-v2__option-chip${selected ? ' is-active' : ''}" ${common}>${escapeHtml(value)}</button>`;
        }).join('');
        const sizeGuide = isSize ? `<button class="byck-v2__size-link" type="button" data-byck-size-chart>${escapeHtml(this.c('sizeChart'))}</button>` : '';
        return `<fieldset class="byck-v2__field"><legend class="visually-hidden">${escapeHtml(option.name)}</legend><div class="byck-v2__field-head"><span class="byck-v2__field-label">${escapeHtml(option.name)}: <strong>${escapeHtml(selectedValue || this.c('chooseOption', { option: option.name }))}</strong></span>${sizeGuide}</div><div class="${isColor ? 'byck-v2__swatches' : 'byck-v2__option-chips'}" role="group" aria-label="${escapeHtml(option.name)}">${controls}</div></fieldset>`;
      }).join('');
    }

    selectOption(index, value) {
      const item = this.editingItem();
      if (!item) return;
      item.selections[index] = value;
      const galleryImages = this.galleryImages();
      this.state.galleryIndex = this.galleryStartingIndex(galleryImages);
      this.els.optionError.hidden = true;
      this.renderOptionFields();
      this.renderGallery();
      const selectedControl = $$(this.els.optionFields, 'button');
      selectedControl.find((button) => Number(button.dataset.byckOptionIndex) === index && button.dataset.byckOptionValue === value)?.focus();
    }

    galleryVariant(item = this.editingItem(), product = this.itemProduct(item)) {
      if (!item || !product) return null;
      const matchesSelection = (variant) => variant.options.every((value, index) => !item.selections[index] || item.selections[index] === value);
      return product.variants.find((variant) => variant.available && matchesSelection(variant))
        || product.variants.find(matchesSelection)
        || this.itemVariant(item)
        || product.variants.find((variant) => variant.available)
        || product.variants[0];
    }

    galleryImages() {
      const item = this.editingItem();
      const product = this.itemProduct(item);
      if (!item || !product) return [];
      const selectedVariant = this.galleryVariant(item, product);
      const colorIndex = product.options.findIndex((option) => option.kind === 'color' || /colou?r/i.test(option.name));
      const selectedColor = colorIndex >= 0 ? (item.selections[colorIndex] || selectedVariant?.options?.[colorIndex] || '') : '';
      // Match the PDP contract exactly: either the merchant label or option value can own the swatch name.
      const galleryGroup = (product.galleryGroups || []).find((group) => group.optionValue === selectedColor || group.label === selectedColor);
      if (galleryGroup?.media?.length) return galleryGroup.media.filter((media) => media?.src);

      const media = [selectedVariant?.featuredMedia, ...(product.media || [])].filter((entry) => entry?.src);
      return media.filter((entry, index) => media.findIndex((candidate) => candidate.src === entry.src) === index);
    }

    mediaKey(src) {
      return String(src || '').replace(/^https?:/, '').replace(/([?&])width=\d+(&?)/, (_match, prefix, suffix) => suffix ? prefix : '').replace(/[?&]$/, '');
    }

    galleryStartingIndex(images) {
      const featuredSrc = this.galleryVariant()?.featuredMedia?.src;
      if (!featuredSrc || !images.length) return 0;
      const featuredKey = this.mediaKey(featuredSrc);
      const index = images.findIndex((image) => this.mediaKey(image.src) === featuredKey);
      return index >= 0 ? index : 0;
    }

    renderGallery() {
      const item = this.editingItem();
      const product = this.itemProduct(item);
      const images = this.galleryImages();
      if (!product || !images.length) { this.els.gallery.innerHTML = ''; return; }
      this.state.galleryIndex = (this.state.galleryIndex + images.length) % images.length;
      const current = images[this.state.galleryIndex];
      const next = images[(this.state.galleryIndex + 1) % images.length];
      this.els.gallery.innerHTML = `<img class="byck-v2__gallery-main" src="${escapeHtml(this.imageUrl(current.src, 900))}" width="900" height="700" alt="${escapeHtml(current.alt || product.title)}"><img class="byck-v2__gallery-next-image" src="${escapeHtml(this.imageUrl(next.src, 360))}" width="360" height="700" alt="" aria-hidden="true"><button type="button" data-byck-gallery-move="-1" aria-label="Previous image">‹</button><button type="button" data-byck-gallery-move="1" aria-label="Next image">›</button><span>${images.map((_, index) => `<button type="button" data-byck-gallery-index="${index}" class="${index === this.state.galleryIndex ? 'is-active' : ''}" aria-label="View image ${index + 1}"></button>`).join('')}</span>`;
    }

    closeOptions(restoreFocus = true) {
      if (this.els.optionDialog.open) this.els.optionDialog.close();
      const reopenReview = this.state.reopenReview;
      this.state.reopenReview = false;
      if (reopenReview) { this.openReview(); return; }
      if (restoreFocus) window.requestAnimationFrame(() => this.state.returnFocus?.focus?.());
    }

    renderSizeChart() {
      const product = this.itemProduct(this.editingItem());
      const guide = product?.sizeGuide;
      if (!product || !guide) return;
      const unit = this.state.sizeUnit;
      const chartHtml = sanitizeGuideHtml(unit === 'cm' ? guide.cmHtml : guide.inchesHtml);
      const fitNoteHtml = sanitizeGuideHtml(guide.fitNote);
      const measureHtml = sanitizeGuideHtml(guide.measureHtml);
      // Allow only the table and instructional structure needed by merchant-owned size guides.
      this.els.sizeContent.innerHTML = `<div class="byck-v2__size-guide-grid"><div class="byck-v2__size-guide-visual"><img src="${escapeHtml(guide.image)}" alt="${escapeHtml(guide.imageAlt || `${product.title} size guide`)}" width="800" height="800" loading="lazy">${fitNoteHtml}</div><div class="byck-v2__size-guide-data"><div class="byck-v2__unit-tabs" role="group" aria-label="Measurement unit"><button type="button" data-byck-size-unit="inches" aria-pressed="${unit === 'inches'}">${escapeHtml(this.c('inches'))}</button><button type="button" data-byck-size-unit="cm" aria-pressed="${unit === 'cm'}">${escapeHtml(this.c('centimeters'))}</button></div><div class="byck-v2__size-table">${chartHtml}</div></div></div><div class="byck-v2__measure-guide">${measureHtml}</div>`;
    }

    openSizeChart() {
      const product = this.itemProduct(this.editingItem());
      if (!product?.sizeGuide) return;
      this.state.sizeUnit = 'inches';
      this.els.sizeTitle.textContent = `${product.title} ${this.c('sizeChart')}`;
      this.renderSizeChart();
      this.state.reopenOptionsAfterSize = true;
      if (this.els.optionDialog.open) this.els.optionDialog.close();
      this.els.sizeDialog.showModal();
      this.els.sizeDialog.querySelector('button')?.focus();
    }

    closeSizeChart() {
      if (this.els.sizeDialog.open) this.els.sizeDialog.close();
      if (this.state.reopenOptionsAfterSize) {
        this.state.reopenOptionsAfterSize = false;
        this.els.optionDialog.showModal();
        this.els.optionDialog.querySelector('[data-byck-size-chart]')?.focus();
      }
    }

    setReviewIsolation(active) {
      if (active) {
        this.els.summary.setAttribute('role', 'dialog');
        this.els.summary.setAttribute('aria-modal', 'true');
        if (this.reviewInertElements?.length) return;
        this.reviewInertElements = [];

        // Inert every sibling along the summary-to-body path so keyboard and
        // screen-reader navigation cannot escape the visual mobile modal.
        let branch = this.els.summary;
        while (branch && branch !== document.body) {
          const parent = branch.parentElement;
          if (!parent) break;
          [...parent.children].forEach((sibling) => {
            if (sibling === branch || sibling.hasAttribute('inert')) return;
            sibling.setAttribute('inert', '');
            this.reviewInertElements.push(sibling);
          });
          branch = parent;
        }
        return;
      }

      this.els.summary.removeAttribute('role');
      this.els.summary.removeAttribute('aria-modal');
      (this.reviewInertElements || []).forEach((element) => element.removeAttribute('inert'));
      this.reviewInertElements = [];
    }

    openReview() {
      this.renderSummary();
      if (!mobile()) {
        this.setReviewIsolation(false);
        this.els.summary.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        this.els.checkout.focus({ preventScroll: true });
        return;
      }
      this.els.summary.classList.add('is-open');
      this.root.classList.add('has-review-open');
      this.setReviewIsolation(true);
      $(this.els.summary, '[data-byck-close-review]')?.focus();
    }

    closeReview(restoreFocus = true) {
      const wasOpen = this.els.summary.classList.contains('is-open');
      this.els.summary.classList.remove('is-open');
      this.root.classList.remove('has-review-open');
      this.setReviewIsolation(false);
      if (restoreFocus && wasOpen) $(this.root, '[data-byck-open-review]')?.focus();
    }

    trap(container, event) {
      if (event.key !== 'Tab') return;
      const focusable = $$(container, 'button:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])').filter((element) => !element.hidden && element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    cartPayloadItems(totals = this.totals()) {
      const instance = this.state.kitInstance;
      const items = this.state.items.map((item) => ({
        id: item.variantId,
        quantity: 1,
        properties: {
          _calm_kit_campaign: CAMPAIGN_ID,
          _calm_kit_instance: instance,
          _calm_kit_line: item.id,
          _calm_kit_role: 'qualifying'
        }
      }));
      this.gifts.filter((gift) => gift.unlockQuantity <= totals.count).forEach((gift) => {
        items.push({
          id: gift.variantId,
          quantity: 1,
          properties: {
            _calm_kit_campaign: CAMPAIGN_ID,
            _calm_kit_instance: instance,
            _calm_kit_line: `gift-${gift.key}-${instance}`,
            _calm_kit_role: 'gift',
            _calm_kit_gift: '1',
            _calm_kit_gift_key: gift.key
          }
        });
      });
      return items;
    }

    async addToCart() {
      const totals = this.totals();
      if (!this.commerceReady || totals.count < 2 || totals.count > MAX_ITEMS || totals.pending > 0) return;
      const items = this.cartPayloadItems(totals);
      this.els.checkout.disabled = true;
      this.els.checkout.textContent = this.c('adding');
      this.els.cartError.hidden = true;
      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items })
        });
        const contentType = response.headers.get('content-type') || '';
        const result = contentType.includes('application/json') ? await response.json() : {};
        if (!response.ok || Number(result?.status) >= 400 || result?.errors) {
          throw new Error(result?.description || result?.message || this.c('cartError'));
        }
        window.location.assign(this.root.dataset.cartUrl || '/cart');
      } catch (error) {
        this.els.cartError.textContent = error.message || this.c('cartError');
        this.els.cartError.hidden = false;
        this.renderSummary();
      }
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.dataset.byckProductMediaIndex !== undefined) {
          this.showProductMedia(button.dataset.byckProductHandle, Number(button.dataset.byckProductMediaIndex));
          return;
        }
        if (button.dataset.byckOptionValue !== undefined) {
          this.selectOption(Number(button.dataset.byckOptionIndex), button.dataset.byckOptionValue);
          return;
        }
        if (button.dataset.byckSizeUnit) {
          this.state.sizeUnit = button.dataset.byckSizeUnit;
          this.renderSizeChart();
          this.els.sizeContent.querySelector(`[data-byck-size-unit="${button.dataset.byckSizeUnit}"]`)?.focus();
          return;
        }
        if (button.dataset.byckFilter) {
          this.state.audience = button.dataset.byckFilter;
          $$(this.root, '[data-byck-filter]').forEach((filter) => filter.setAttribute('aria-pressed', String(filter === button)));
          this.renderProducts();
        }
        if (button.dataset.byckAddProduct) this.addProduct(button.dataset.byckAddProduct, button);
        if (button.dataset.byckDecrement) this.decrementProduct(button.dataset.byckDecrement);
        if (button.dataset.byckEditItem) this.openOptions(button.dataset.byckEditItem, button, mobile() && this.els.summary.classList.contains('is-open'));
        if (button.dataset.byckConfigureItem) this.openOptions(button.dataset.byckConfigureItem, button, false);
        if (button.dataset.byckRemoveItem) this.removeItem(button.dataset.byckRemoveItem);
        if (button.hasAttribute('data-byck-open-review')) {
          const pendingItem = this.state.items.find((item) => !item.complete);
          if (pendingItem) this.openOptions(pendingItem.id, button, false);
          else this.openReview();
        }
        if (button.hasAttribute('data-byck-close-review')) this.closeReview();
        if (button.hasAttribute('data-byck-close-options')) this.closeOptions();
        if (button.hasAttribute('data-byck-size-chart')) this.openSizeChart();
        if (button.hasAttribute('data-byck-close-size')) this.closeSizeChart();
        if (button.dataset.byckGalleryMove) { this.state.galleryIndex += Number(button.dataset.byckGalleryMove); this.renderGallery(); }
        if (button.dataset.byckGalleryIndex) { this.state.galleryIndex = Number(button.dataset.byckGalleryIndex); this.renderGallery(); }
        if (button.hasAttribute('data-byck-add-to-cart')) this.addToCart();
      });

      this.els.products.addEventListener('pointerdown', (event) => {
        const media = event.target.closest('[data-byck-product-media]');
        if (!media || event.target.closest('button') || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.productSwipe = { pointerId: event.pointerId, handle: media.dataset.byckProductMedia, x: event.clientX, y: event.clientY };
        media.setPointerCapture?.(event.pointerId);
      });
      this.els.products.addEventListener('pointerup', (event) => {
        const swipe = this.productSwipe;
        this.productSwipe = null;
        if (!swipe || swipe.pointerId !== event.pointerId) return;
        const deltaX = event.clientX - swipe.x;
        const deltaY = event.clientY - swipe.y;
        if (Math.abs(deltaX) < 36 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        event.preventDefault();
        this.moveProductMedia(swipe.handle, deltaX < 0 ? 1 : -1);
      });
      this.els.products.addEventListener('pointercancel', () => { this.productSwipe = null; });

      this.els.optionForm.addEventListener('change', (event) => {
        const index = Number(event.target.dataset.byckOptionIndex);
        if (Number.isNaN(index)) return;
        const item = this.editingItem();
        item.selections[index] = event.target.value;
        this.renderOptionFields();
        this.renderGallery();
        this.els.optionFields.querySelector(`[data-byck-option-index="${index}"]`)?.focus();
      });

      this.els.optionForm.addEventListener('submit', (event) => {
        event.preventDefault();
        const item = this.editingItem();
        const product = this.itemProduct(item);
        const missingIndex = product.options.findIndex((_, index) => !item.selections[index]);
        if (missingIndex >= 0) {
          this.els.optionError.textContent = this.c('chooseEveryOption');
          this.els.optionError.hidden = false;
          this.els.optionFields.querySelector(`[data-byck-option-index="${missingIndex}"]:not([disabled])`)?.focus();
          return;
        }
        const variant = product.variants.find((candidate) => candidate.available && candidate.options.every((value, index) => value === item.selections[index]));
        if (!variant) {
          this.els.optionError.textContent = this.c('unavailableCombination');
          this.els.optionError.hidden = false;
          return;
        }
        item.variantId = variant.id;
        item.complete = true;
        const reopenReviewAfterFlow = this.state.reopenReview;
        this.renderAll();
        this.toast(this.c('productConfirmed', { product: product.title }));

        // Keep multi-item setup continuous when more than one hoodie still needs options.
        const nextItem = this.state.items.find((entry) => !entry.complete);
        if (nextItem) {
          const nextTrigger = $$(this.root, '[data-byck-configure-item]').find((candidate) => candidate.dataset.byckConfigureItem === nextItem.id);
          this.openOptions(nextItem.id, nextTrigger, reopenReviewAfterFlow);
          return;
        }
        this.closeOptions();
      });

      this.els.optionDialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeOptions(); });
      this.els.sizeDialog.addEventListener('cancel', (event) => { event.preventDefault(); this.closeSizeChart(); });
      [this.els.optionDialog, this.els.sizeDialog].forEach((dialog) => dialog.addEventListener('keydown', (event) => this.trap(dialog, event)));
      this.els.summary.addEventListener('keydown', (event) => { if (mobile() && this.els.summary.classList.contains('is-open')) this.trap(this.els.summary, event); });
      document.addEventListener('keydown', this.escapeHandler = (event) => {
        if (event.key === 'Escape' && mobile() && this.els.summary.classList.contains('is-open')) this.closeReview();
      });
    }

    destroy() {
      window.clearTimeout(this.toastTimer);
      window.clearInterval(this.countdownTimer);
      this.setReviewIsolation(false);
      document.removeEventListener('keydown', this.escapeHandler);
      klaviyoSuppression.stop(this.root);
    }
  }

  const selector = '[data-build-your-calm-kit][data-configuration-available="true"]';
  const initialize = async (root) => {
    try { await window.GroundingPriceTest.ready; } catch { return; }
    if (root?.matches?.(selector) && !root.__calmKitBuilder) root.__calmKitBuilder = new CalmKitBuilder(root);
    $$(root || document, selector).forEach((section) => {
      if (!section.__calmKitBuilder) section.__calmKitBuilder = new CalmKitBuilder(section);
    });
  };
  const destroy = (root) => {
    const sections = root?.matches?.(selector) ? [root, ...$$(root, selector)] : $$(root || document, selector);
    sections.forEach((section) => { section.__calmKitBuilder?.destroy(); delete section.__calmKitBuilder; });
  };

  document.addEventListener('DOMContentLoaded', () => initialize(document));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
